import { Input, RadioGroup, SubmitButton } from '@panwds/react-form';
import { Button, Link } from '@panwds/react-ui';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslate } from "../../customHooks";
import { isEmpty } from 'lodash';
import { composeValidators, isBlockedDomain, isEmail, isRequired } from '../../utils/validate';
import { toast } from '../../components';
import getUrlParameter from "../../utils/GetUrlParameter";
import { oktaProvider } from "../../authProvider";
import queryString from "query-string";
import styles from './styles/Register.module.scss';
import { useCreateSubscriptionMutation } from '../../redux/services/subscriptions-service';
import CircularLoader from '../../components/CircularLoader/CircularLoader';
import { FormattedMessage } from 'react-intl';
import { pendoUpdate } from '../Pendo';
import { useValidateAssociationCodeMutation } from '../../redux/services/validate-scm-code-service';
import { useCallback, useState } from 'react';

export const SubscriptionCreate = (props: any) => {
    const translate = useTranslate();
    const [createSubscription, { isLoading }] = useCreateSubscriptionMutation();
    const ACCOUNT_ID = getUrlParameter("acc-id");
    const REGISTRATION_TOKEN = getUrlParameter("x-amzn-marketplace-token");
    const [nextStep, setNextStep] = useState<{ UserName: string; BillingAssociationCode?: string } | undefined>()

    const [triggerValidateScmCode, { isLoading: validatingScmCode }] = useValidateAssociationCodeMutation();

    const formMethods = useForm({});
    const {
        formState: { dirtyFields }, watch
    } = formMethods

    const callSubscriptionCreate = useCallback(async (params) => {
        const { FirstName, LastName, UserName, BillingAssociationCode } = params;
        const subscriptionPayload = {
            UserName,
            RegistrationToken: REGISTRATION_TOKEN,
            ...(FirstName && { FirstName }),
            ...(LastName && { LastName }),
            ...(BillingAssociationCode && { Bac: BillingAssociationCode })
        };
        try {
            const result = await createSubscription({ payload: subscriptionPayload }).unwrap();
            if (result) {
                pendoUpdate({ ...params });
                // Store the data returned by createSubscription for the redirect
                if (result?.PanwidEmailSent === false) {
                    const options = {
                        state: {
                            ...(queryString?.parse(window?.location?.search)),
                            ...(BillingAssociationCode && { BillingAssociationCode })
                        },
                        loginHint: UserName
                    };
                    oktaProvider.signIn(options);
                }
                else {
                    setNextStep({
                        UserName: UserName,
                        BillingAssociationCode: BillingAssociationCode
                    });
                }
            }
        } catch (error: any) {
            toast.error(`${error?.code}: ${error?.error}`, { toastId: "create-firewall" });
        }
    }, [])

    const handleSubmit = async (params: any = {}) => {
        if (!REGISTRATION_TOKEN) {
            toast.error(
                "Registration Token Missing. Please go to AWS Marketplace and follow the instructions to set up your account!"
            );
        }
        else {
            if (params?.BillingAssociationCode) {
                try {
                    const isValidScmCode = await triggerValidateScmCode({
                        payload: {
                            UserEmail: params?.UserName,
                            BillingAccessCode: params?.BillingAssociationCode,
                            AWSAccountID: params?.AccountId
                        }
                    }).unwrap();
                    if (isValidScmCode) {
                        callSubscriptionCreate(params);
                    }
                } catch (err) {
                    toast.error(err?.error, { toastId: "validate-scm-code" });
                }
            }
            else {
                callSubscriptionCreate(params);
            }
        }
    };
    const hasCode = watch("HasBillingAssociationCode");

    return (
        <>
            <div className="tw-text-center tw-py-4">
                {translate('auth.title')}
            </div>
            {(isLoading || validatingScmCode) && <CircularLoader loading={true} />}
            {nextStep && nextStep.UserName ? (
                <div className="formLayout">
                    <div>
                        <span className={styles.subTitle}>
                            <FormattedMessage
                                id={"activate.message"}
                                values={{
                                    break: <div className="tw-py-2" />,
                                    link: <Link
                                        dataMetrics="cloudngfw-get-login-help"
                                        external
                                        href="https://www.paloaltonetworks.com/services/support/login-assistance"
                                    >
                                        {translate(`activate.getHelp`)}
                                    </Link>
                                }} />
                        </span>
                    </div>
                    <Button
                        addClassName={`${styles.customButton} tw-mt-4`}
                        size="lg"
                        data-metrics="cloudngfw-sign-up-link-new-tenant-button"
                        type="submit"
                        onClick={
                            () => {
                                const options = {
                                    state: {
                                        ...(queryString?.parse(window?.location?.search)),
                                        ...(props?.scmBillingAssociationCode && { BillingAssociationCode: props.scmBillingAssociationCode })
                                    }
                                };
                                props.closeModal();
                                oktaProvider.signIn(options);
                            }
                        }
                    >
                        {translate("activate.login")}
                    </Button>
                </div>
            ) :
                (<FormProvider {...formMethods}>
                    <form onSubmit={formMethods.handleSubmit(handleSubmit)}>
                        <div className={isLoading ? "loadingBlur formLayout" : "formLayout"}>
                            <Input
                                name="AccountId"
                                label={translate("generic.awsAccountId")}
                                dataMetrics="cloudngfw-sign-up-first-name"
                                requiredAsterisk
                                value={ACCOUNT_ID}
                                readOnly
                            />

                            <RadioGroup horizontal
                                label={translate(`auth.haveBillingAssociationCode`)}
                                name="HasBillingAssociationCode"
                                dataMetrics={"cloudngfw-sign-up-has-billing-association-code"}
                                requiredAsterisk
                                tooltip={translate("auth.billingAssociationCodeTooltip")}
                                items={[{
                                    value: 'Yes',
                                    children: translate('generic.yes'),
                                }, {
                                    value: 'No',
                                    children: translate('generic.no'),
                                }]}
                            />

                            <Input
                                name="UserName"
                                type="email"
                                label={translate("auth.email")}
                                dataMetrics="cloudngfw-sign-up-email"
                                requiredAsterisk
                                register={{
                                    validate: (value) => composeValidators(isRequired, isBlockedDomain, isEmail)(value)
                                }}
                            />

                            {hasCode === "Yes" &&
                                <Input
                                    name="BillingAssociationCode"
                                    label={translate("auth.billingAssociationCode")}
                                    dataMetrics="cloudngfw-scm-sign-up-billing-code"
                                    requiredAsterisk
                                />
                            }
                            {hasCode === "No" &&
                                <>
                                    <Input
                                        name="FirstName"
                                        label={translate("auth.name")}
                                        dataMetrics="cloudngfw-sign-up-first-name"
                                        requiredAsterisk
                                    />

                                    <Input
                                        name="LastName"
                                        label={translate("auth.lastName")}
                                        dataMetrics="cloudngfw-sign-up-last-name"
                                        requiredAsterisk
                                    />
                                </>
                            }

                            <SubmitButton addClassName={styles.customButton} disabled={isEmpty(dirtyFields)}>{translate(`generic.save`)}</SubmitButton>
                        </div>
                    </form>
                </FormProvider>)
            }
        </>
    );
};
